import { useMemo, useState, useEffect } from "react";
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  CircularProgress,
  Divider,
  TablePagination,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { getLineItemsColumns } from "./columns";
import { CancelLineButton } from "./ConfirmationDialogs";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import httpclient from "../../../Utils";
import CustomLoadingOverlay from "../../CustomLoadingOverlay";

/**
 * Server-side paginated order line items table
 * This component now manages its own pagination and fetches its own data.
 */
const OrderLineItemsTable = ({ jobId, onRefresh }) => {
  const [orderLinesData, setOrderLinesData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed for MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableLoading, setTableLoading] = useState(false);

  const [editingLineId, setEditingLineId] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [pendingSave, setPendingSave] = useState(null);
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: tokenRefresh } = useTokenRefresh();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchOrderLines = async () => {
    if (!jobId) {
      setOrderLinesData([]);
      setTotalRows(0);
      return;
    }

    setTableLoading(true);
    const API_URL = `/orders/${jobId}/details?page=${currentPage + 1}&pagination=${rowsPerPage}`;

    try {
      const res = await httpclient.get(API_URL);
      const { data } = res;

      if (data.status === 200) {
        setOrderLinesData(data.data.orderLines.data || []);
        setTotalRows(data.data.orderLines.total || 0);
      } else {
        showNotification("error", data.msg);
        setOrderLinesData([]);
        setTotalRows(0);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        tokenRefresh();
      } else {
        handleApiError(error);
      }
      setOrderLinesData([]);
      setTotalRows(0);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderLines();
  }, [jobId, currentPage, rowsPerPage]);

  const hasStatusCanceled = useMemo(() => {
    return orderLinesData.some((line) => line.line_state === "canceled");
  }, [orderLinesData]);

  const showActionsColumn = useMemo(() => {
    if (!orderLinesData || orderLinesData.length === 0) return false;
    return orderLinesData.some((line) => line.line_state !== "canceled");
  }, [orderLinesData]);

  const isQtyModified = useMemo(() => {
    return orderLinesData.some((line) => line.qty_modified_reason);
  }, [orderLinesData]);

  const hasCanceledReason = useMemo(() => {
    return orderLinesData.some((line) => line.canceled_reason);
  }, [orderLinesData]);

  const lineItemsColumns = getLineItemsColumns(
    hasStatusCanceled,
    showActionsColumn,
    isQtyModified,
    hasCanceledReason,
  );

  const handleEditQuantity = (lineId, qty) => {
    setEditingLineId(lineId);
    setEditQuantity(qty.toString());
  };

  const handleSaveQuantity = async (lineId) => {
    const quantity = parseInt(editQuantity);
    if (isNaN(quantity) || quantity < 0) {
      showNotification("error", "Please enter a valid quantity");
      return;
    }

    // Find the current line item to get previous quantity
    const currentLine = orderLinesData.find((line) => line.line_id === lineId);

    // Store the pending save data and show reason dialog
    setPendingSave({
      lineId,
      quantity,
      previousQuantity: currentLine?.order_qty || 0,
    });
    setShowReasonDialog(true);
    setReasonText("");
  };

  const handleReasonSubmit = async () => {
    if (reasonText.length < 5) {
      showNotification("error", "Reason must be at least 5 characters");
      return;
    }

    if (!pendingSave) return;

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("quantity", pendingSave.quantity);
      formData.append("order_qty_update_reason_code", reasonText);

      const endpoint = `/orders/${pendingSave.lineId}/update-order-item`;
      const res = await httpclient.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { data } = res;
      if (data.status === 200) {
        showNotification("success", "Quantity updated successfully");
        fetchOrderLines(); // Re-fetch data after successful update
        setEditingLineId(null);
        setEditQuantity("");
        setShowReasonDialog(false);
        setPendingSave(null);
        setReasonText("");
      } else {
        showNotification("error", data.message || "Failed to update quantity");
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReasonDialogClose = () => {
    setShowReasonDialog(false);
    setPendingSave(null);
    setReasonText("");
  };

  const handleCancelEdit = () => {
    setEditingLineId(null);
    setEditQuantity("");
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0); // Reset to first page when changing rows per page
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontFamily: "Lexend",
          fontSize: "20px",
          mb: 2,
          fontWeight: 500,
          color: "#383E49",
        }}
      >
        Order Line
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          mb: 1.5,
        }}
      >
        <InfoIcon sx={{ fontSize: "0.875rem", marginRight: "8px" }} />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.75rem",
            textAlign: "left",
            marginRight: "16px",
            color: "green",
          }}
        >
          Green - Picked
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ marginRight: "16px" }} />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.75rem",
            textAlign: "left",
            marginRight: "16px",
            color: "#FF0000",
          }}
        >
          Red - Canceled
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ marginRight: "16px" }} />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.75rem",
            textAlign: "left",
            marginRight: "16px",
            color: "#be8800",
          }}
        >
          Yellow - Freight
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ marginRight: "16px" }} />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.75rem",
            textAlign: "left",
          }}
        >
          White - Not Picked
        </Typography>
      </Box>

      <TableContainer sx={{ mb: 3, position: "relative" }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              {lineItemsColumns.map((column) => (
                <TableCell key={column.id} align={column.align || "left"}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "14px", fontWeight: 500, color: "#667085" }}
                  >
                    {column.header}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableLoading ? (
              <TableRow sx={{ height: lineItemsColumns.length * 53 }}>
                <TableCell colSpan={lineItemsColumns.length} align="center">
                  <CustomLoadingOverlay loadingText="" />
                </TableCell>
              </TableRow>
            ) : (
              orderLinesData.map((item) => (
                <TableRow
                  key={item.line_id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor:
                      item.line_state === "canceled"
                        ? "#ffebee"
                        : item?.order_line_color_satate || "default",
                  }}
                >
                  <TableCell component="th" scope="row">
                    {item.stock_code}
                  </TableCell>
                  <TableCell>{item.stock_description}</TableCell>
                  <TableCell>{item.bin_bay_location}</TableCell>
                  <TableCell align="center">{item.stock_on_hand}</TableCell>
                  <TableCell align="center">
                    {editingLineId === item.line_id ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <TextField
                          size="small"
                          value={editQuantity}
                          onChange={(e) => setEditQuantity(e.target.value)}
                          type="number"
                          inputProps={{ min: 0 }}
                          sx={{ width: 70 }}
                          autoFocus
                        />
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={() => handleSaveQuantity(item.line_id)}
                            color="primary"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            color="secondary"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : item.item_updated_button_enabled === 1 ? (
                      <Box
                        sx={{
                          padding: "8px 12px",
                          borderRadius: "6px",
                          minHeight: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          backgroundColor: "#f8f9ff",
                          border: "1px solid #e3e8ff",
                          color: "#4338ca",
                          fontWeight: 500,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            backgroundColor: "#eef2ff",
                            borderColor: "#c7d2fe",
                            transform: "translateY(-1px)",
                            boxShadow: "0 2px 4px rgba(67, 56, 202, 0.1)",
                          },
                        }}
                        onClick={() =>
                          handleEditQuantity(item.line_id, item.modified_qty)
                        }
                        title="Click to edit quantity"
                      >
                        {item.order_qty}
                        <EditOutlinedIcon
                          sx={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            fontSize: "14px",
                            color: "#4338ca",
                            opacity: 0.6,
                          }}
                        />
                      </Box>
                    ) : (
                      item.order_qty
                    )}
                  </TableCell>
                  <TableCell align="center">{item.modified_qty}</TableCell>
                  <TableCell align="center">{item.picked_qty}</TableCell>
                  {hasStatusCanceled && (
                    <TableCell align="center">
                      {item.line_state === "canceled" && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Canceled"
                          sx={{
                            backgroundColor: "#ffebee",
                            color: "#d32f2f",
                            borderColor: "#ffcdd2",
                          }}
                        />
                      )}
                    </TableCell>
                  )}
                  {showActionsColumn && (
                    <TableCell align="center">
                      {item.line_state !== "canceled" &&
                        item?.item_canceled_button_enabled === 1 && (
                          <CancelLineButton
                            lineId={item.line_id}
                            onCancel={() => {
                              showNotification(
                                "success",
                                "Order line cancelled successfully",
                              );
                              fetchOrderLines(); // Re-fetch data after successful cancellation
                            }}
                          />
                        )}
                    </TableCell>
                  )}
                  {isQtyModified && (
                    <TableCell align="center">
                      {item?.qty_modified_reason}
                    </TableCell>
                  )}
                  {hasCanceledReason && (
                    <TableCell align="center">
                      {item?.canceled_reason}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalRows}
        page={currentPage}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 20, 50]}
      />

      {/* Reason Selection Dialog */}
      <Dialog
        open={showReasonDialog}
        onClose={handleReasonDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Update Quantity
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Quantity Information */}
          <Box
            sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Quantity Update Information
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Previous Quantity
                </Typography>
                <Typography variant="h6" color="text.primary">
                  {pendingSave?.previousQuantity || 0}
                </Typography>
              </Box>
              <Box sx={{ mx: 2, color: "text.secondary" }}>→</Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Updated Quantity
                </Typography>
                <Typography variant="h6" color="primary.main">
                  {pendingSave?.quantity || 0}
                </Typography>
              </Box>
            </Box>
          </Box>

          <FormControl fullWidth>
            <FormLabel sx={{ mb: 2, color: "text.primary" }}>
              Please enter a reason for updating the quantity:
            </FormLabel>
            <TextField
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 50 }}
              placeholder="Enter reason (5-50 characters)"
            />
            <Typography
              variant="caption"
              color={
                reasonText.length < 5 || reasonText.length > 50
                  ? "error"
                  : "textSecondary"
              }
              sx={{ mt: 1, display: "block", textAlign: "right" }}
            >
              {reasonText.length}/50 characters
              {reasonText.length < 5 && " (minimum 5 characters required)"}
            </Typography>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleReasonDialogClose}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleReasonSubmit}
            color="primary"
            variant="contained"
            disabled={
              reasonText.length < 5 || reasonText.length > 50 || isSubmitting
            }
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderLineItemsTable;
